import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Play, Loader2, Moon, RefreshCw, Bell, Check, Timer, XCircle, AlertTriangle, StopCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
// Types for Task Node
export type TaskStatus = 'pending' | 'ready' | 'running' | 'sleeping' | 'cycling' | 'attention' | 'completed' | 'waiting' | 'cancelled' | 'failed';
export interface TaskNodeProps {
  id: string;
  name: string;
  status: TaskStatus;
  agent?: {
    name: string;
    avatar?: string;
  };
  meta?: string; // e.g., duration, wake time, iteration
  onClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onStop?: (e: React.MouseEvent) => void; // Stop button handler for running nodes
  selected?: boolean;
}
// Base container for all nodes
const NodeContainer = ({
  children,
  className,
  onClick,
  onContextMenu,
  selected
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  selected?: boolean;
}) => <motion.div whileHover={{
  scale: 1.02,
  y: -2
}} whileTap={{
  scale: 0.98
}} onClick={onClick} onContextMenu={onContextMenu} className={cn('relative w-[180px] h-[80px] rounded-xl border p-3 cursor-pointer transition-all duration-200 flex flex-col justify-between shadow-sm hover:shadow-md', selected ? 'ring-2 ring-blue-500 border-transparent' : '', className)}>
    {children}
  </motion.div>;
// Agent Avatar Component
const AgentAvatar = ({
  name
}: {
  name?: string;
}) => {
  if (!name) return null;
  return <div className="w-5 h-5 rounded-full bg-surface-700 border border-surface-500 flex items-center justify-center text-[10px] text-text-primary overflow-hidden" title={name}>
      {name.charAt(0)}
    </div>;
};
// 1. Pending Node
export const TaskNodePending = ({
  name,
  onClick,
  onContextMenu,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-surface-700 border-surface-400 border-dashed hover:border-surface-300 hover:bg-surface-600">
    <div className="flex justify-between items-start">
      <span className="text-xs font-medium text-text-secondary line-clamp-2">
        {name}
      </span>
      <Clock className="w-4 h-4 text-text-muted" />
    </div>
    <div className="flex items-center gap-2 mt-auto">
      <span className="text-[10px] text-text-muted uppercase tracking-wider">
        Pending
      </span>
    </div>
  </NodeContainer>;
// 2. Ready Node
export const TaskNodeReady = ({
  name,
  agent,
  onClick,
  onContextMenu,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-surface-900 border-primary-500/50 hover:border-primary-400 hover:shadow-blue-900/20">
    <div className="flex justify-between items-start">
      <span className="text-xs font-medium text-text-primary line-clamp-2">
        {name}
      </span>
      <Play className="w-4 h-4 text-primary-400" />
    </div>
    <div className="flex items-center justify-between mt-auto">
      <span className="text-[10px] text-primary-400 uppercase tracking-wider font-medium">
        Ready
      </span>
      <AgentAvatar name={agent?.name} />
    </div>
  </NodeContainer>;
// 3. Running Node
export const TaskNodeRunning = ({
  name,
  agent,
  onClick,
  onContextMenu,
  onStop,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-blue-950/30 border-primary-500 hover:shadow-blue-900/30 group/running">
    <div className="absolute inset-0 rounded-xl bg-primary-500/5 animate-pulse" />
    {/* Stop button overlay - appears on hover */}
    {onStop && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onStop(e);
        }}
        className="absolute inset-0 rounded-xl bg-red-950/80 opacity-0 group-hover/running:opacity-100 transition-opacity z-20 flex items-center justify-center gap-2"
        title="Stop execution"
      >
        <StopCircle className="w-5 h-5 text-red-400" />
        <span className="text-sm font-medium text-red-300">Stop</span>
      </button>
    )}
    <div className="relative flex justify-between items-start z-10">
      <span className="text-xs font-medium text-primary-100 line-clamp-2">
        {name}
      </span>
      <Loader2 className="w-4 h-4 text-primary-400 animate-spin" />
    </div>
    <div className="relative flex items-center justify-between mt-auto z-10">
      <span className="text-[10px] text-primary-300 uppercase tracking-wider font-medium">
        Running
      </span>
      <AgentAvatar name={agent?.name} />
    </div>
  </NodeContainer>;
// 4. Sleeping Node
export const TaskNodeSleeping = ({
  name,
  meta,
  onClick,
  onContextMenu,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-purple-950/20 border-purple-500/30 hover:border-purple-500/60">
    <div className="flex justify-between items-start">
      <span className="text-xs font-medium text-purple-200 line-clamp-2">
        {name}
      </span>
      <Moon className="w-4 h-4 text-purple-400" />
    </div>
    <div className="flex items-center justify-between mt-auto">
      <span className="text-[10px] text-purple-400 uppercase tracking-wider font-medium">
        {meta || 'Sleeping'}
      </span>
    </div>
  </NodeContainer>;
// 5. Cycling Node
export const TaskNodeCycling = ({
  name,
  meta,
  onClick,
  onContextMenu,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-cyan-950/20 border-cyan-500/30 hover:border-cyan-500/60">
    <div className="flex justify-between items-start">
      <span className="text-xs font-medium text-cyan-200 line-clamp-2">
        {name}
      </span>
      <RefreshCw className="w-4 h-4 text-cyan-400 animate-spin-slow" style={{
      animationDuration: '3s'
    }} />
    </div>
    <div className="flex items-center justify-between mt-auto">
      <span className="text-[10px] text-cyan-400 uppercase tracking-wider font-medium">
        Polling
      </span>
      {meta && <span className="px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px]">
          {meta}
        </span>}
    </div>
  </NodeContainer>;
// 6. Attention Node
export const TaskNodeAttention = ({
  name,
  meta,
  onClick,
  onContextMenu,
  onStop,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-orange-950/20 border-warning-500 hover:shadow-orange-900/20 group/attention">
    {/* Stop button overlay - appears on hover */}
    {onStop && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onStop(e);
        }}
        className="absolute inset-0 rounded-xl bg-red-950/80 opacity-0 group-hover/attention:opacity-100 transition-opacity z-20 flex items-center justify-center gap-2"
        title="Stop execution"
      >
        <StopCircle className="w-5 h-5 text-red-400" />
        <span className="text-sm font-medium text-red-300">Stop</span>
      </button>
    )}
    <div className="flex justify-between items-start">
      <span className="text-xs font-medium text-warning-200 line-clamp-2">
        {name}
      </span>
      <Bell className="w-4 h-4 text-warning-500 fill-orange-500/20 animate-pulse" />
    </div>
    <div className="flex items-center justify-between mt-auto">
      <span className="text-[10px] text-warning-400 uppercase tracking-wider font-medium truncate max-w-[120px]">
        {meta || 'Needs Input'}
      </span>
    </div>
  </NodeContainer>;
// 7. Completed Node
export const TaskNodeCompleted = ({
  name,
  meta,
  agent,
  onClick,
  onContextMenu,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-green-950/20 border-success-500/30 hover:border-success-500/60">
    <div className="flex justify-between items-start">
      <span className="text-xs font-medium text-success-200 line-clamp-2">
        {name}
      </span>
      <div className="w-4 h-4 rounded-full bg-success-500/20 flex items-center justify-center">
        <Check className="w-3 h-3 text-success-500" />
      </div>
    </div>
    <div className="flex items-center justify-between mt-auto">
      <span className="text-[10px] text-success-500 uppercase tracking-wider font-medium">
        {meta || 'Done'}
      </span>
      <AgentAvatar name={agent?.name} />
    </div>
  </NodeContainer>;
// 8. Waiting Node (Fan-out - blocked on children)
export const TaskNodeWaiting = ({
  name,
  meta,
  onClick,
  onContextMenu,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-amber-950/20 border-amber-500/50 hover:border-amber-400">
    <div className="flex justify-between items-start">
      <span className="text-xs font-medium text-amber-200 line-clamp-2">
        {name}
      </span>
      <Timer className="w-4 h-4 text-amber-400" />
    </div>
    <div className="flex items-center justify-between mt-auto">
      <span className="text-[10px] text-amber-400 uppercase tracking-wider font-medium">
        {meta || 'Waiting'}
      </span>
    </div>
  </NodeContainer>;
// 9. Cancelled Node (Stopped early - terminal)
export const TaskNodeCancelled = ({
  name,
  meta,
  agent,
  onClick,
  onContextMenu,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-surface-800/50 border-surface-500/30 hover:border-surface-500/50 opacity-60">
    <div className="flex justify-between items-start">
      <span className="text-xs font-medium text-text-muted line-clamp-2 line-through">
        {name}
      </span>
      <XCircle className="w-4 h-4 text-surface-400" />
    </div>
    <div className="flex items-center justify-between mt-auto">
      <span className="text-[10px] text-surface-400 uppercase tracking-wider font-medium">
        {meta || 'Cancelled'}
      </span>
      <AgentAvatar name={agent?.name} />
    </div>
  </NodeContainer>;
// 10. Failed Node (Errored - terminal)
export const TaskNodeFailed = ({
  name,
  meta,
  agent,
  onClick,
  onContextMenu,
  selected
}: TaskNodeProps) => <NodeContainer onClick={onClick} onContextMenu={onContextMenu} selected={selected} className="bg-red-950/20 border-red-500/50 hover:border-red-400">
    <div className="flex justify-between items-start">
      <span className="text-xs font-medium text-red-200 line-clamp-2">
        {name}
      </span>
      <AlertTriangle className="w-4 h-4 text-red-400" />
    </div>
    <div className="flex items-center justify-between mt-auto">
      <span className="text-[10px] text-red-400 uppercase tracking-wider font-medium">
        {meta || 'Failed'}
      </span>
      <AgentAvatar name={agent?.name} />
    </div>
  </NodeContainer>;
// Main Component that dispatches to specific variants
export const TaskNode = (props: TaskNodeProps) => {
  switch (props.status) {
    case 'pending':
      return <TaskNodePending {...props} />;
    case 'ready':
      return <TaskNodeReady {...props} />;
    case 'running':
      return <TaskNodeRunning {...props} />;
    case 'sleeping':
      return <TaskNodeSleeping {...props} />;
    case 'cycling':
      return <TaskNodeCycling {...props} />;
    case 'attention':
      return <TaskNodeAttention {...props} />;
    case 'completed':
      return <TaskNodeCompleted {...props} />;
    case 'waiting':
      return <TaskNodeWaiting {...props} />;
    case 'cancelled':
      return <TaskNodeCancelled {...props} />;
    case 'failed':
      return <TaskNodeFailed {...props} />;
    default:
      return <TaskNodePending {...props} />;
  }
};